import React, { Suspense, lazy } from "react";
import { motion, MotionConfig } from "framer-motion";
import { LogIn, Rocket, ArrowRight, Sparkles, Brain, Users, Shield } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { HeroNeuralBackdrop } from "../components/HeroNeuralBackdrop";
import { HeroRotatingTagline } from "../components/HeroRotatingTagline";
import { Logo3DFallback } from "../components/Logo3DViewer/Logo3DFallback";
import { cn } from "../../../components/ui/utils";

const Logo3DViewer = lazy(() =>
  import("../components/Logo3DViewer").then((m) => ({ default: m.Logo3DViewer }))
);

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const HEADLINE = [
  { text: "Da gestão escolar ao", gradient: false },
  { text: "aprendizado personalizado.", gradient: true },
];

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: SPRING },
  },
};

const wordVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: SPRING },
  },
};

const logoVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.75, delay: 0.3, ease: SPRING },
  },
};

const proofPoints = [
  {
    icon: Brain,
    text: "IA como assistente pedagógico — regras determinísticas no núcleo, sem caixa-preta.",
  },
  {
    icon: Users,
    text: "Alunos, professores, coordenadores e famílias no mesmo painel — separados por papel.",
  },
  {
    icon: Shield,
    text: "Aprendizado e gamificação de um lado; notas e avaliação formal de outro — LGPD na base.",
  },
];

interface HeroSectionProps {
  heroSectionRef: React.RefObject<HTMLElement | null>;
  heroScrollProgress: number;
  onLogin: () => void;
  onDemoCta: () => void;
}

export function HeroSection({
  heroSectionRef,
  heroScrollProgress,
  onLogin,
  onDemoCta,
}: HeroSectionProps) {
  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={heroSectionRef}
        className="relative overflow-hidden border-b border-white/[0.06]"
      >
        {/* Background */}
        <HeroNeuralBackdrop scrollProgress={heroScrollProgress} />

        <div className="container relative z-10 mx-auto grid max-w-7xl gap-8 py-10 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:gap-12 sm:px-6 sm:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14 lg:py-24">

          {/* Left — text content */}
          <motion.div
            className="min-w-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Badge */}
            <motion.div variants={itemVariants}>
              <Badge className="badge-glow mb-5 border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-sm text-teal-200">
                <Sparkles className="mr-1.5 inline h-3.5 w-3.5" aria-hidden />
                ERP educacional · personalização por perfil
              </Badge>
            </motion.div>

            {/* Rotating tagline */}
            <motion.div variants={itemVariants}>
              <HeroRotatingTagline />
            </motion.div>

            {/* Mobile logo — 3D N otimizado, hidden on desktop */}
            <motion.div
              variants={itemVariants}
              className="my-6 flex justify-center lg:hidden"
            >
              <Suspense fallback={<Logo3DFallback />}>
                <Logo3DViewer />
              </Suspense>
            </motion.div>

            {/* Headline with word stagger */}
            <motion.h1
              className="text-balance font-display text-[1.6rem] font-bold leading-[1.2] tracking-tight text-white min-[400px]:text-[1.9rem] sm:text-4xl sm:leading-[1.18] md:text-5xl lg:text-[3.2rem]"
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.045 } } }}
            >
              {HEADLINE.map((part, partIdx) => (
                <React.Fragment key={partIdx}>
                  {part.text.split(" ").map((word, wordIdx) => (
                    <motion.span
                      key={`${partIdx}-${wordIdx}`}
                      variants={wordVariants}
                      className={cn(
                        "inline-block mr-[0.28em] last:mr-0",
                        part.gradient &&
                          "bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent"
                      )}
                    >
                      {word}
                    </motion.span>
                  ))}
                  {partIdx < HEADLINE.length - 1 && (
                    <span className="inline-block w-full" aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-slate-300 sm:mt-6 sm:text-lg"
              variants={itemVariants}
            >
              Gestão de turmas, notas, professores e financeiro em um só lugar. IA que
              adapta{" "}
              <strong className="font-medium text-slate-100">como</strong> o conteúdo
              aparece e <strong className="font-medium text-slate-100">como</strong> o
              ritmo evolui — com regras auditáveis, não achismo algorítmico.
            </motion.p>

            {/* Proof points */}
            <motion.ul className="mt-6 space-y-3 text-sm text-slate-300 sm:mt-8" variants={itemVariants}>
              {proofPoints.map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                    <p.icon className="h-4 w-4 text-teal-300" aria-hidden />
                  </span>
                  <span className="min-w-0 pt-0.5 leading-snug">{p.text}</span>
                </li>
              ))}
            </motion.ul>

            {/* CTAs */}
            <motion.div
              className="mt-8 flex w-full flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center"
              variants={itemVariants}
            >
              <Button
                size="lg"
                onClick={onDemoCta}
                className="landing-cta-glow h-auto min-h-12 w-full whitespace-normal bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-3 text-sm font-semibold leading-snug shadow-xl shadow-teal-950/50 hover:from-teal-400 hover:to-cyan-500 sm:h-14 sm:min-h-0 sm:w-auto sm:min-w-[12rem] sm:px-8 sm:text-base"
              >
                <Rocket className="mr-2 inline h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden />
                Solicitar demonstração
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => scrollToId("demonstracao")}
                className="h-12 w-full bg-white/5 px-6 text-sm text-white ring-1 ring-inset ring-white/25 backdrop-blur hover:bg-white/10 hover:ring-white/40 sm:h-14 sm:w-auto sm:px-8 sm:text-base"
              >
                Ver o que muda na prática
                <ArrowRight className="ml-2 h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden />
              </Button>
            </motion.div>

            {/* Login link */}
            <motion.p
              className="mt-5 text-sm text-slate-400 sm:mt-6"
              variants={itemVariants}
            >
              <span className="text-slate-500">Já tem conta?</span>{" "}
              <button
                type="button"
                onClick={onLogin}
                className="group relative inline-flex items-center gap-1 font-semibold text-teal-400 transition-colors hover:text-teal-300"
              >
                <span className="relative inline-block">
                  Entrar
                  <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-teal-400/90 transition-all duration-300 group-hover:w-full" />
                </span>
                <LogIn className="h-3.5 w-3.5 opacity-80 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
              </button>
            </motion.p>

            <motion.p
              className="mt-4 text-xs leading-relaxed text-slate-500 sm:mt-5"
              variants={itemVariants}
            >
              Cadastro institucional sem cartão — manifestação de interesse; nosso time
              retorna com próximos passos.
            </motion.p>
          </motion.div>

          {/* Right — 3D logo (desktop only — mobile uses fallback above) */}
          <motion.div
            className="hidden items-center justify-center min-w-0 lg:flex"
            variants={logoVariants}
            initial="hidden"
            animate="visible"
          >
            <Suspense fallback={<Logo3DFallback />}>
              <Logo3DViewer />
            </Suspense>
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
